// Type definitions for payments module

export type PaymentStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REFUNDED' | 'PARTIALLY_REFUNDED' | 'CANCELLED' | 'EXPIRED';
export type PaymentMethod = 'PIX' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'CASH' | 'DIGITAL_WALLET';
export type PaymentType = 'SERVICE' | 'PRODUCT' | 'DEPOSIT' | 'TIP' | 'REFUND';

export interface Payment {
  id: string;
  businessId: string;
  transactionId: string;
  customerId?: string;
  customerName: string;
  customerPhone?: string;
  bookingId?: string;
  orderId?: string;
  amount: number;
  currency: string;
  status: PaymentStatus;
  method: PaymentMethod;
  type: PaymentType;
  description?: string;
  cardLast4?: string;
  cardBrand?: string;
  createdAt: Date;
  completedAt?: Date;
}

export interface PaymentFilters {
  status?: string;
  method?: string;
  type?: string;
  searchQuery?: string;
}

export const DEFAULT_PAYMENT_FILTERS: PaymentFilters = {
  status: 'ALL',
  method: 'ALL',
  type: 'ALL',
  searchQuery: '',
};

export const PAYMENT_METHOD_COLORS: Record<PaymentMethod, string> = {
  PIX: '#00BDAE',
  CREDIT_CARD: '#6366F1',
  DEBIT_CARD: '#8B5CF6',
  CASH: '#10B981',
  DIGITAL_WALLET: '#F59E0B',
};

export interface PaymentStats {
  todayAmount: number;
  todayCount: number;
  completedAmount: number;
  pendingAmount: number;
  refundedAmount: number;
  byStatus: Record<string, number>;
}

export function calculatePaymentStats(payments: Payment[]): PaymentStats {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todayPayments = payments.filter(p => {
    const d = new Date(p.createdAt);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  });

  const byStatus: Record<string, number> = {};
  for (const p of payments) {
    byStatus[p.status] = (byStatus[p.status] || 0) + 1;
  }

  return {
    todayAmount: todayPayments.reduce((sum, p) => sum + p.amount, 0),
    todayCount: todayPayments.length,
    completedAmount: payments.filter(p => p.status === 'COMPLETED').reduce((sum, p) => sum + p.amount, 0),
    pendingAmount: payments.filter(p => p.status === 'PENDING' || p.status === 'PROCESSING').reduce((sum, p) => sum + p.amount, 0),
    refundedAmount: payments.filter(p => p.status === 'REFUNDED' || p.status === 'PARTIALLY_REFUNDED').reduce((sum, p) => sum + p.amount, 0),
    byStatus,
  };
}

export function formatPaymentMethod(method: PaymentMethod): string {
  const labels: Record<PaymentMethod, string> = {
    PIX: 'PIX',
    CREDIT_CARD: 'Credit Card',
    DEBIT_CARD: 'Debit Card',
    CASH: 'Cash',
    DIGITAL_WALLET: 'Digital Wallet',
  };
  return labels[method];
}

export function getPaymentMethodIcon(method: PaymentMethod): string {
  const icons: Record<PaymentMethod, string> = {
    PIX: '⚡',
    CREDIT_CARD: '💳',
    DEBIT_CARD: '💳',
    CASH: '💵',
    DIGITAL_WALLET: '📱',
  };
  return icons[method];
}
